import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/hooks/useAuth';
import { portfolioApi, tenantApi, usersApi, ApiClientError, errorMessage } from '@/lib/api';
import { portfolioLabel } from '@/lib/config';
import { queryKeys } from '@/lib/api/queryKeys';
import { onboardingSchema, type OnboardingFormValues } from './schema';
import { SubdomainStep } from './steps/SubdomainStep';
import { TemplateStep } from './steps/TemplateStep';
import { ProfileStep } from './steps/ProfileStep';
import type { HeroValues } from '../editor/schemas';

type StepIndex = 0 | 1 | 2;

const STEP_META: ReadonlyArray<{
  label: string;
  title: string;
  hint: string;
  fields: ReadonlyArray<keyof OnboardingFormValues>;
}> = [
  {
    label: 'Address',
    title: 'Claim your subdomain.',
    hint: 'This is where your work will live on the internet.',
    fields: ['subdomain'],
  },
  {
    label: 'Template',
    title: 'Pick a starting template.',
    hint: 'You can switch templates at any time, your content carries over.',
    fields: ['template', 'theme'],
  },
  {
    label: 'Profile',
    title: 'The short of it.',
    hint: 'Three fields. We prefill everything else from here.',
    fields: ['name', 'headline', 'location'],
  },
];

export function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState<StepIndex>(0);
  const [subdomainAvailable, setSubdomainAvailable] = useState(false);

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    mode: 'onTouched',
    defaultValues: {
      subdomain: '',
      template: 'minimal',
      theme: 'ink',
      name: user?.name ?? '',
      headline: '',
      location: '',
    },
  });

  const submit = useMutation({
    mutationFn: async (values: OnboardingFormValues) => {
      const tenant = await tenantApi.setSubdomain(values.subdomain);
      await usersApi.updateMe({
        name: values.name.trim(),
        headline: values.headline?.trim() || null,
        location: values.location?.trim() || null,
      });
      await portfolioApi.updateSettings({
        template: values.template,
        theme: values.theme,
        enabledSections: ['hero', 'about', 'projects', 'experience', 'education', 'contact'],
      });
      const trimmedName = values.name.trim();
      const heroSeed: HeroValues = {
        portraitUrl: '',
        tagline: '',
        headline: trimmedName.length >= 3 ? trimmedName : `${trimmedName} · Portfolio`,
        subheadline: values.headline?.trim() || '',
        ctaLabel: '',
        ctaHref: '',
        availability: 'available',
      };
      const portfolio = await portfolioApi.upsertSection('hero', heroSeed);
      return { tenant, portfolio };
    },
    onSuccess: ({ tenant, portfolio }) => {
      qc.setQueryData(queryKeys.tenant, tenant);
      qc.setQueryData(queryKeys.portfolio, portfolio);
      qc.invalidateQueries({ queryKey: queryKeys.me });
      toast.success('Portfolio created', {
        description: `Your site is live at ${portfolioLabel(tenant.subdomain)}`,
      });
      navigate('/app', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.status === 409) {
        form.setError('subdomain', { message: 'This subdomain is already taken' });
        setStep(0);
        return;
      }
      toast.error('We could not complete setup', {
        description: errorMessage(err, 'Please try again.'),
      });
    },
  });

  async function handleNext() {
    const fields = STEP_META[step].fields;
    const valid = await form.trigger(fields);
    if (!valid) return;
    if (step === 0 && !subdomainAvailable) {
      form.setError('subdomain', { message: 'This subdomain is not available' });
      return;
    }
    if (step < 2) {
      setStep((step + 1) as StepIndex);
      return;
    }
    const values = form.getValues();
    submit.mutate(values);
  }

  function handleBack() {
    if (step === 0) return;
    setStep((step - 1) as StepIndex);
  }

  const meta = STEP_META[step];
  const canAdvance =
    step === 0
      ? subdomainAvailable
      : step === 1
        ? Boolean(form.watch('template'))
        : form.watch('name').trim().length > 1;

  return (
    <FormProvider {...form}>
      <div className="flex min-h-screen flex-col bg-canvas text-ink">
        <header className="flex items-center justify-between border-b border-line px-8 py-5">
          <Logo />
          <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
            <span>
              Step {String(step + 1).padStart(2, '0')} /{' '}
              {String(STEP_META.length).padStart(2, '0')}
            </span>
            <span className="h-3 w-px bg-line" />
            <span className="text-ink-muted">{user?.email}</span>
          </div>
        </header>

        <div className="mx-auto w-full max-w-3xl flex-1 px-6 py-12 md:py-20">
          <nav aria-label="Onboarding progress" className="mb-12">
            <ol className="flex items-center gap-2">
              {STEP_META.map((s, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <li key={s.label} className="flex flex-1 items-center gap-3">
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[11px] tracking-tight ${
                        active
                          ? 'border-ink bg-ink text-board'
                          : done
                            ? 'border-ink/30 bg-board text-ink'
                            : 'border-line bg-board text-ink-faint'
                      }`}
                    >
                      {done ? '✓' : String(i + 1).padStart(2, '0')}
                    </span>
                    <span
                      className={`hidden text-xs font-medium uppercase tracking-[0.18em] md:block ${
                        active ? 'text-ink' : 'text-ink-faint'
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < STEP_META.length - 1 ? (
                      <span className="ml-2 hidden h-px flex-1 bg-line md:block" />
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </nav>

          <div className="mb-10 max-w-xl">
            <h1 className="mb-3 font-display text-3xl font-semibold tracking-tight text-ink md:text-4xl">
              {meta.title}
            </h1>
            <p className="text-[15px] text-ink-muted">{meta.hint}</p>
          </div>

          <div className="mb-10">
            {step === 0 ? (
              <SubdomainStep onAvailabilityChange={setSubdomainAvailable} />
            ) : step === 1 ? (
              <TemplateStep />
            ) : (
              <ProfileStep />
            )}
          </div>

          <footer className="flex items-center justify-between border-t border-line pt-6">
            <Button variant="ghost" size="md" onClick={handleBack} disabled={step === 0}>
              ← Back
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleNext}
              disabled={!canAdvance || submit.isPending}
            >
              {submit.isPending ? 'Creating…' : step === 2 ? 'Finish setup' : 'Continue →'}
            </Button>
          </footer>
        </div>
      </div>
    </FormProvider>
  );
}
